import React, { useState } from "react";
import styled from "styled-components";
import { EyeIcon, EyeSlashIcon } from "./Icons";

const borderColors = {
  default: "#CED4DA",
  hover: "#004F96",
  active: "#00396C",
  filled: "#ADB5BD",
  success: "#049F2F",
  error: "#EC2D30",
  disabled: "#CED4DA",
};

const TextField = ({
  icon,
  placeholder = "Placeholder text",
  type = "text",
  onClear,
  disabled,
  error,
  errorMessage,
  success,
  maxLength = 100,
  showCharacterLimit = false,
  isTextArea = false,
  value,
  onChange,
  height = null,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseOver = () => !disabled && setIsHovered(true);
  const handleMouseOut = () => !disabled && setIsHovered(false);
  const handleFocus = () => !disabled && setIsFocused(true);
  const handleBlur = () => !disabled && setIsFocused(false);

  const getCurrentState = () => {
    if (disabled) return "disabled";
    if (error) return "error";
    if (success) return "success";
    if (isFocused) return "active";
    if (isHovered) return "hover";
    if (value && value.length > 0) return "filled";
    return "default";
  };

  const inputType = type === "password" && showPassword ? "text" : type;

  return (
    <>
      <TextFieldWrapper
        $state={getCurrentState()}
        disabled={disabled}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        <InputArea>
          {icon && <span className="icon">{icon}</span>}
          {isTextArea ? (
            <TextArea
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              maxLength={maxLength}
              $height={height}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              {...props}
            />
          ) : (
            <TextInput
              type={inputType}
              placeholder={placeholder}
              value={value}
              onChange={onChange}
              maxLength={maxLength}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              {...props}
            />
          )}
          {type === "password" && (
            <span
              className="toggle-password"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </span>
          )}
          {onClear && (
            <span className="clear-icon" onClick={onClear}>
              X
            </span>
          )}
        </InputArea>
      </TextFieldWrapper>
      {/* Display the error message if there is an error */}
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {showCharacterLimit && (
        <CharacterLimit
          className="body-text-regular regular"
          $maxLength={maxLength}
        >
          <span className="text">Max character limit</span>
          <span className="count">{`${value.length}/${maxLength}`}</span>
        </CharacterLimit>
      )}
    </>
  );
};

export default TextField;

const TextFieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 12px;
  background: ${(props) => (props.disabled ? "#F8F9FA" : "#FFF")};
  border: 1px solid
    ${(props) => borderColors[props.$state] || borderColors.default};
  transition: border-color 0.3s ease-in-out;
  position: relative;

  & .icon {
    margin-right: 8px;
    color: #6c757d;
  }

  & .clear-icon {
    cursor: pointer;
    color: #6c757d;
  }
`;

const InputArea = styled.div`
  display: flex;
  align-items: center;
  padding: 0;
  flex: 1;
`;

const TextInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 16px;
  color: #161718;
  padding: 12px 16px;
  background: transparent;
  width: 100%;
  line-height: 20px;
  box-sizing: border-box;

  &::placeholder {
    color: #adb5bd;
  }
`;

const TextArea = styled.textarea`
  border: none;
  outline: none;
  flex: 1;
  font-size: 16px;
  padding: 12px 16px;
  color: #161718;
  background: transparent;
  width: 100%;
  resize: none;
  overflow-y: auto;
  box-sizing: border-box;
  line-height: 20px;
  ${({ $height }) => ($height ? `height: ${$height};` : "")}

  &::placeholder {
    color: #adb5bd;
  }
`;

const CharacterLimit = styled.div`
  color: #6c757d;
  padding-top: 8px;
  align-self: flex-end;
  display: flex;
  justify-content: space-between;

  .text {
    flex-shrink: 0; /* Prevents shrinking */
  }

  .count {
    flex-shrink: 0; /* Prevents shrinking */
    text-align: right;
    min-width: ${(props) => (props.$maxLength > 999 ? "90px" : "70px")};
  }
`;

const ErrorMessage = styled.span`
  color: #ec2d30; /* Match the error border color */
  font-size: 12px;
  margin-top: 4px;
`;
